import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, Phone, ShieldCheck, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { startBankIdFlow } from '@/lib/bankid';
import {
  startPhoneAttach,
  verifyPhoneAttach,
  startEmailAttach,
  verifyEmailAttach,
} from '@/lib/loginMethods';

const inputClassName =
  'flex h-10 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/**
 * "Inloggning"-kortet på profilsidan: visar kontots inloggningsmetoder
 * (e-post, telefon, BankID) med status, och låter användaren lägga till de
 * som saknas. Varje tillägg är ägarskapsbevisat — engångskod via SMS för
 * telefon, engångskod via mejl för e-post, färsk BankID-verifiering för
 * BankID (via /bankid-callback i link-läge → api-bankid-link).
 */
export function LoginMethodsCard() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [expanded, setExpanded] = useState<'email' | 'phone' | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [pendingPhone, setPendingPhone] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [codeValue, setCodeValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Status läses från egen profilrad (synkas från auth av databastrigger).
  // Hashen exponeras aldrig i UI:t — bara om den finns.
  const { data: methods, isLoading } = useQuery({
    queryKey: ['login-methods', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('profiles_v2')
        .select('phone, personal_number_hash')
        .eq('id', user!.id)
        .maybeSingle();
      return {
        phone: data?.phone ?? null,
        hasBankId: !!data?.personal_number_hash,
      };
    },
    enabled: !!user?.id,
  });

  // Syntetiska adresser (bankid+…@ziplink.local) är interna och visas inte.
  const email = user?.email && !user.email.endsWith('@ziplink.local') ? user.email : null;
  // Äldre rader kan sakna plustecknet — visa alltid internationellt format.
  const rawPhone = methods?.phone ?? user?.phone ?? null;
  const phone = rawPhone ? (rawPhone.startsWith('+') ? rawPhone : `+${rawPhone}`) : null;

  const openAttach = (method: 'email' | 'phone') => {
    setExpanded(method);
    setInputValue('');
    setCodeValue('');
    setPendingPhone(null);
    setPendingEmail(null);
    setError(null);
  };

  const handleStartAttach = async () => {
    setBusy(true);
    setError(null);
    try {
      if (expanded === 'phone') {
        setPendingPhone(await startPhoneAttach(inputValue));
      } else if (expanded === 'email') {
        setPendingEmail(await startEmailAttach(inputValue));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Något gick fel. Försök igen.');
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyPhone = async () => {
    if (!pendingPhone) return;
    setBusy(true);
    setError(null);
    try {
      await verifyPhoneAttach(pendingPhone, codeValue);
      setExpanded(null);
      queryClient.invalidateQueries({ queryKey: ['login-methods', user?.id] });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fel kod. Försök igen.');
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (!pendingEmail) return;
    setBusy(true);
    setError(null);
    try {
      await verifyEmailAttach(pendingEmail, codeValue);
      // E-postvisningen kommer från user.email — supabase-js fyrar USER_UPDATED
      // efter verifieringen, så AuthContext uppdaterar den automatiskt.
      setExpanded(null);
      queryClient.invalidateQueries({ queryKey: ['login-methods', user?.id] });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fel kod. Försök igen.');
    } finally {
      setBusy(false);
    }
  };

  const handleLinkBankId = async () => {
    setLinking(true);
    setError(null);

    const result = await startBankIdFlow({ linkMode: true });
    if (!result.ok) {
      setError(result.message);
    }
    setTimeout(() => setLinking(false), 1500);
  };

  const renderAttachForm = (method: 'email' | 'phone') => {
    if (expanded !== method) return null;

    const pendingTarget = method === 'phone' ? pendingPhone : pendingEmail;
    if (pendingTarget) {
      return (
        <div className="space-y-2 pl-8">
          <p className="text-xs text-muted-foreground">
            Ange koden vi skickade till {pendingTarget}
          </p>
          <input
            type="text"
            inputMode="numeric"
            value={codeValue}
            onChange={(e) => setCodeValue(e.target.value.replace(/\D/g, ''))}
            placeholder="000000"
            className={inputClassName}
          />
          <Button
            size="sm"
            onClick={method === 'phone' ? handleVerifyPhone : handleVerifyEmail}
            disabled={busy || codeValue.length < 6}
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Verifiera
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-2 pl-8">
        <input
          type={method === 'email' ? 'email' : 'tel'}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={method === 'email' ? 'namn@exempel.se' : '07X-XXX XX XX'}
          className={inputClassName}
        />
        <Button size="sm" onClick={handleStartAttach} disabled={busy || !inputValue.trim()}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Skicka kod
        </Button>
      </div>
    );
  };

  return (
    <Card className="rounded-3xl">
      <CardContent className="p-4 space-y-4">
        <p className="text-xs text-muted-foreground uppercase tracking-wide">Inloggning</p>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <Mail className="h-5 w-5 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">E-post</p>
                <p className="text-sm font-medium truncate">{email ?? 'Ej tillagd'}</p>
              </div>
            </div>
            {!email && expanded !== 'email' && (
              <Button variant="outline" size="sm" onClick={() => openAttach('email')}>
                Lägg till
              </Button>
            )}
          </div>
          {renderAttachForm('email')}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <Phone className="h-5 w-5 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Telefon</p>
                <p className="text-sm font-medium truncate">{phone ?? 'Ej tillagd'}</p>
              </div>
            </div>
            {!phone && expanded !== 'phone' && (
              <Button variant="outline" size="sm" onClick={() => openAttach('phone')}>
                Lägg till
              </Button>
            )}
          </div>
          {renderAttachForm('phone')}
        </div>

        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <ShieldCheck className="h-5 w-5 text-muted-foreground shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">BankID</p>
              <p className="text-sm font-medium">
                {isLoading ? 'Laddar...' : methods?.hasBankId ? 'Kopplat' : 'Ej kopplat'}
              </p>
            </div>
          </div>
          {!isLoading && !methods?.hasBankId && (
            <Button variant="outline" size="sm" onClick={handleLinkBankId} disabled={linking}>
              {linking ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Startar BankID...
                </>
              ) : (
                'Koppla BankID'
              )}
            </Button>
          )}
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <p className="text-xs text-muted-foreground">
          Varje inloggningsmetod verifieras innan den aktiveras — med
          engångskod eller BankID. Personnumret lagras aldrig i klartext,
          endast som skyddat kontrollvärde.
        </p>
      </CardContent>
    </Card>
  );
}
